import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "@/lib/api/client";
import {
  SUPERVISOR_CHATS_REFRESH,
  supervisorChatNav,
  type SupervisorConversation,
} from "../supervisor-chat-nav";
import type { RagNavConfig } from "@/apps/rag/rag-nav";

function sameConversations(left: SupervisorConversation[], right: SupervisorConversation[]): boolean {
  if (left.length !== right.length) return false;
  return left.every(
    (item, index) => item.id === right[index]?.id && item.title === right[index]?.title
  );
}

export function useSupervisorChatNav(): RagNavConfig | null {
  const { pathname } = useLocation();
  const [config, setConfig] = useState<RagNavConfig | null>(null);
  const conversationsRef = useRef<SupervisorConversation[]>([]);

  useEffect(() => {
    if (!pathname.startsWith("/chat")) {
      conversationsRef.current = [];
      setConfig(null);
      return;
    }
    let cancelled = false;
    function apply(next: SupervisorConversation[]) {
      if (sameConversations(conversationsRef.current, next)) return;
      conversationsRef.current = next;
      setConfig(supervisorChatNav(next));
    }
    async function load() {
      try {
        const data = await apiFetch<{ conversations?: SupervisorConversation[] }>(
          "/api/supervisor-chat/conversations"
        );
        if (!cancelled) apply(data.conversations ?? []);
      } catch {
        if (!cancelled) apply([]);
      }
    }
    void load();
    window.addEventListener(SUPERVISOR_CHATS_REFRESH, load);
    return () => {
      cancelled = true;
      window.removeEventListener(SUPERVISOR_CHATS_REFRESH, load);
    };
  }, [pathname]);

  return pathname.startsWith("/chat") ? config : null;
}
